// ============================================================================
// LocationLibrary — radio picker over the location library.
// ============================================================================
// Sits inside EventForm above the Location text input. Picking a location
// fills both the location_details (= address) and venue_details fields of
// the event. A "Manage library" button opens LocationManageModal.
//
// Unlike presenters (multi-select), an event has ONE venue, so this is a
// single-select pill picker. "Clear selection" puts the form back into
// manual-entry mode.
// ============================================================================

import { useEffect, useState } from 'react';
import { Settings2, RefreshCw, MapPin } from 'lucide-react';
import { api, type Location } from '../api';
import { LocationManageModal } from './LocationManageModal';

interface Props {
  token: string;
  // Whatever's currently typed in the location field. Used to highlight
  // a library match if the address matches.
  currentAddress: string;
  onPick: (loc: Location) => void;
  disabled?: boolean;
}

export function LocationLibrary({ token, currentAddress, onPick, disabled }: Props) {
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [manageOpen, setManageOpen] = useState(false);

  const load = () => {
    setLoading(true);
    setError('');
    api
      .locations()
      .then((d) => setLocations(d.locations ?? []))
      .catch((err) => setError(err?.message || 'Could not load locations.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  // Mark a row as "current" if the form's address field matches its
  // address exactly. Helps UIF spot when an event already uses a library
  // entry without having to re-pick.
  const currentMatch = (l: Location) =>
    (l.address ?? '').trim().toLowerCase() === currentAddress.trim().toLowerCase();

  return (
    <div className="border border-gray-200 rounded-md p-3 bg-gray-50 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
          Location library
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            disabled={loading || disabled}
            title="Refresh list"
            className="inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-white disabled:opacity-50"
          >
            <RefreshCw className={'h-3 w-3 ' + (loading ? 'animate-spin' : '')} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setManageOpen(true)}
            disabled={disabled}
            className="inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-white disabled:opacity-50"
          >
            <Settings2 className="h-3 w-3" />
            Manage library
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded px-2 py-1 text-xs">{error}</div>
      )}

      {loading && locations.length === 0 ? (
        <p className="text-sm text-gray-500 italic">Loading locations…</p>
      ) : locations.length === 0 ? (
        <p className="text-sm text-gray-500 italic">
          No locations yet. Click <em>Manage library</em> to add one.
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {locations.map((l) => {
            const isCurrent = currentMatch(l);
            return (
              <button
                key={l.location_id}
                type="button"
                onClick={() => onPick(l)}
                disabled={disabled}
                className={
                  'flex items-start gap-2 text-left rounded-md border p-2 transition-colors ' +
                  (isCurrent
                    ? 'border-uif-green bg-uif-green/10'
                    : 'border-gray-300 bg-white hover:border-uif-green/40')
                }
              >
                <MapPin className="h-4 w-4 text-uif-green-dark flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-900 truncate">{l.name}</div>
                  {l.address && <div className="text-xs text-gray-600 truncate">{l.address}</div>}
                </div>
                {isCurrent && (
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-uif-green-dark flex-shrink-0">
                    Current
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      <p className="text-[11px] text-gray-500">
        Picking a location fills the address + venue notes below. Hand-edit afterwards if you need to.
      </p>

      <LocationManageModal
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        token={token}
        locations={locations}
        onRefresh={load}
      />
    </div>
  );
}
